// src/services/companyService.ts
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { getAuth } from 'firebase/auth';

const COMPANY_PROFILE_COLLECTION = 'companyProfiles';

export interface CompanyAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface CompanyContactInfo {
  phone: string;
  email: string;
  website: string;
  fax: string;
}

export interface CompanySocialMedia {
  facebook: string;
  instagram: string;
  threads: string;
}

export interface CompanyLocation {
  latitude: number;
  longitude: number;
  address: string;
}

export interface CompanyProfile {
  id?: string;
  companyName: string;
  businessType: string;
  registrationNumber: string;
  taxId: string;
  address: CompanyAddress;
  contactInfo: CompanyContactInfo;
  socialMedia: CompanySocialMedia;
  location: CompanyLocation;
  currency: string;
  timezone: string;
  fiscalYearStart: string;
  taxPercentage: number;
  logo: string;
  notes: string;
  invoiceFooter?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  createdBy?: string;
  updatedBy?: string;
}

export class CompanyService {
  /**
   * Get the company profile
   * For simplicity, we assume one company profile per account
   * You can modify this to support multiple companies if needed
   */
  static async getCompanyProfile(): Promise<CompanyProfile | null> {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('You must be logged in to view company profile');
      }

      // Get all company profiles (in this implementation, we expect only one)
      const companiesRef = collection(db, COMPANY_PROFILE_COLLECTION);
      const snapshot = await getDocs(companiesRef);
      
      if (snapshot.empty) {
        return null;
      }

      // Get the first (and presumably only) company profile
      const doc = snapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        companyName: data.companyName || '',
        businessType: data.businessType || '',
        registrationNumber: data.registrationNumber || '',
        taxId: data.taxId || '',
        address: data.address || {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
        },
        contactInfo: data.contactInfo || {
          phone: '',
          email: '',
          website: '',
          fax: '',
        },
        socialMedia: data.socialMedia || {
          facebook: '',
          instagram: '',
          threads: '',
        },
        location: data.location || {
          latitude: 6.9271,  // Default to Colombo, Sri Lanka
          longitude: 79.8612,
          address: '',
        },
        currency: data.currency || 'USD',
        timezone: data.timezone || 'America/New_York',
        fiscalYearStart: data.fiscalYearStart || '01-01',
        taxPercentage: data.taxPercentage || 0,
        logo: data.logo || '',
        notes: data.notes || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        createdBy: data.createdBy,
        updatedBy: data.updatedBy,
      };
    } catch (error: any) {
      console.error('Error fetching company profile:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to access company data.');
      }
      throw error;
    }
  }

  /**
   * Get company profile by ID
   */
  static async getCompanyProfileById(companyId: string): Promise<CompanyProfile | null> {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('You must be logged in to view company profile');
      }

      const companyRef = doc(db, COMPANY_PROFILE_COLLECTION, companyId);
      const companySnap = await getDoc(companyRef);
      
      if (!companySnap.exists()) {
        return null;
      }

      const data = companySnap.data();
      
      return {
        id: companySnap.id,
        companyName: data.companyName || '',
        businessType: data.businessType || '',
        registrationNumber: data.registrationNumber || '',
        taxId: data.taxId || '',
        address: data.address || {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
        },
        contactInfo: data.contactInfo || {
          phone: '',
          email: '',
          website: '',
          fax: '',
        },
        socialMedia: data.socialMedia || {
          facebook: '',
          instagram: '',
          threads: '',
        },
        location: data.location || {
          latitude: 6.9271,  // Default to Colombo, Sri Lanka
          longitude: 79.8612,
          address: '',
        },
        currency: data.currency || 'USD',
        timezone: data.timezone || 'America/New_York',
        fiscalYearStart: data.fiscalYearStart || '01-01',
        taxPercentage: data.taxPercentage || 0,
        logo: data.logo || '',
        notes: data.notes || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        createdBy: data.createdBy,
        updatedBy: data.updatedBy,
      };
    } catch (error: any) {
      console.error('Error fetching company profile:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to access company data.');
      }
      throw error;
    }
  }

  /**
   * Create a new company profile
   */
  static async createCompanyProfile(profile: CompanyProfile): Promise<string> {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('You must be logged in to create a company profile');
      }

      // Check if a company profile already exists
      const existingProfile = await this.getCompanyProfile();
      if (existingProfile) {
        throw new Error('A company profile already exists. Please update the existing profile instead.');
      }

      // Validate tax percentage
      if (profile.taxPercentage < 0 || profile.taxPercentage > 100) {
        throw new Error('Tax percentage must be between 0 and 100');
      }

      // Validate social media URLs if provided
      if (profile.socialMedia.facebook) {
        this.validateUrl(profile.socialMedia.facebook, 'Facebook');
      }
      if (profile.socialMedia.instagram) {
        this.validateUrl(profile.socialMedia.instagram, 'Instagram');
      }
      if (profile.socialMedia.threads) {
        this.validateUrl(profile.socialMedia.threads, 'Threads');
      }

      // Validate location coordinates
      if (!this.validateLatitude(profile.location.latitude)) {
        throw new Error('Invalid latitude. Must be between -90 and 90');
      }
      if (!this.validateLongitude(profile.location.longitude)) {
        throw new Error('Invalid longitude. Must be between -180 and 180');
      }

      // Create new company profile document
      const companyRef = doc(collection(db, COMPANY_PROFILE_COLLECTION));
      
      await setDoc(companyRef, {
        companyName: profile.companyName,
        businessType: profile.businessType || '',
        registrationNumber: profile.registrationNumber || '',
        taxId: profile.taxId || '',
        address: profile.address,
        contactInfo: profile.contactInfo,
        socialMedia: {
          facebook: profile.socialMedia.facebook || '',
          instagram: profile.socialMedia.instagram || '',
          threads: profile.socialMedia.threads || '',
        },
        location: {
          latitude: profile.location.latitude,
          longitude: profile.location.longitude,
          address: profile.location.address || '',
        },
        currency: profile.currency || 'USD',
        timezone: profile.timezone || 'America/New_York',
        fiscalYearStart: profile.fiscalYearStart || '01-01',
        taxPercentage: profile.taxPercentage || 0,
        logo: profile.logo || '',
        notes: profile.notes || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        updatedBy: currentUser.uid,
      });

      return companyRef.id;
    } catch (error: any) {
      console.error('Error creating company profile:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to create company data.');
      }
      throw error;
    }
  }

  /**
   * Update existing company profile
   */
  static async updateCompanyProfile(profile: CompanyProfile): Promise<void> {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('You must be logged in to update company profile');
      }

      // Validate tax percentage
      if (profile.taxPercentage < 0 || profile.taxPercentage > 100) {
        throw new Error('Tax percentage must be between 0 and 100');
      }

      // Validate social media URLs if provided
      if (profile.socialMedia.facebook) {
        this.validateUrl(profile.socialMedia.facebook, 'Facebook');
      }
      if (profile.socialMedia.instagram) {
        this.validateUrl(profile.socialMedia.instagram, 'Instagram');
      }
      if (profile.socialMedia.threads) {
        this.validateUrl(profile.socialMedia.threads, 'Threads');
      }

      // Validate location coordinates
      if (!this.validateLatitude(profile.location.latitude)) {
        throw new Error('Invalid latitude. Must be between -90 and 90');
      }
      if (!this.validateLongitude(profile.location.longitude)) {
        throw new Error('Invalid longitude. Must be between -180 and 180');
      }

      // Get the existing company profile to find its ID
      const existingProfile = await this.getCompanyProfile();
      if (!existingProfile || !existingProfile.id) {
        throw new Error('No company profile found to update');
      }

      const companyRef = doc(db, COMPANY_PROFILE_COLLECTION, existingProfile.id);
      
      await updateDoc(companyRef, {
        companyName: profile.companyName,
        businessType: profile.businessType || '',
        registrationNumber: profile.registrationNumber || '',
        taxId: profile.taxId || '',
        address: profile.address,
        contactInfo: profile.contactInfo,
        socialMedia: {
          facebook: profile.socialMedia.facebook || '',
          instagram: profile.socialMedia.instagram || '',
          threads: profile.socialMedia.threads || '',
        },
        location: {
          latitude: profile.location.latitude,
          longitude: profile.location.longitude,
          address: profile.location.address || '',
        },
        currency: profile.currency || 'USD',
        timezone: profile.timezone || 'America/New_York',
        fiscalYearStart: profile.fiscalYearStart || '01-01',
        taxPercentage: profile.taxPercentage || 0,
        logo: profile.logo || '',
        notes: profile.notes || '',
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      });
    } catch (error: any) {
      console.error('Error updating company profile:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to update company data.');
      }
      throw error;
    }
  }

  /**
   * Delete company profile
   * Note: Use with caution - this will permanently delete the company profile
   */
  static async deleteCompanyProfile(): Promise<void> {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('You must be logged in to delete company profile');
      }

      // Check user permissions (only admin should be able to delete)
      // You can add role checking here if needed

      const existingProfile = await this.getCompanyProfile();
      if (!existingProfile || !existingProfile.id) {
        throw new Error('No company profile found to delete');
      }

      const companyRef = doc(db, COMPANY_PROFILE_COLLECTION, existingProfile.id);
      
      // Instead of deleting, you might want to archive it
      await updateDoc(companyRef, {
        isActive: false,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      });
    } catch (error: any) {
      console.error('Error deleting company profile:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to delete company data.');
      }
      throw error;
    }
  }

  /**
   * Validate URL format
   */
  static validateUrl(url: string, platform: string): void {
    const urlPattern = /^https?:\/\/.+/i;
    if (!urlPattern.test(url)) {
      throw new Error(`${platform} URL must start with http:// or https://`);
    }
  }

  /**
   * Validate latitude
   */
  static validateLatitude(lat: number): boolean {
    return lat >= -90 && lat <= 90;
  }

  /**
   * Validate longitude
   */
  static validateLongitude(lng: number): boolean {
    return lng >= -180 && lng <= 180;
  }

  /**
   * Validate fiscal year start format
   */
  static validateFiscalYearStart(fiscalYearStart: string): boolean {
    const regex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
    return regex.test(fiscalYearStart);
  }

  /**
   * Validate tax percentage
   */
  static validateTaxPercentage(taxPercentage: number): boolean {
    return taxPercentage >= 0 && taxPercentage <= 100;
  }

  /**
   * Calculate tax amount from a subtotal
   */
  static calculateTaxAmount(subtotal: number, taxPercentage: number): number {
    if (!this.validateTaxPercentage(taxPercentage)) {
      throw new Error('Invalid tax percentage');
    }
    return (subtotal * taxPercentage) / 100;
  }

  /**
   * Calculate total with tax
   */
  static calculateTotalWithTax(subtotal: number, taxPercentage: number): number {
    const taxAmount = this.calculateTaxAmount(subtotal, taxPercentage);
    return subtotal + taxAmount;
  }

  /**
   * Format currency
   */
  static formatCurrency(amount: number, currencyCode: string): string {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).format(amount);
    } catch (error) {
      return `${currencyCode} ${amount.toFixed(2)}`;
    }
  }

  /**
   * Get company display name (for use in headers, reports, etc.)
   */
  static async getCompanyDisplayName(): Promise<string> {
    try {
      const profile = await this.getCompanyProfile();
      return profile?.companyName || 'Company';
    } catch (error) {
      console.error('Error getting company display name:', error);
      return 'Company';
    }
  }

  /**
   * Get default tax percentage for the company
   */
  static async getDefaultTaxPercentage(): Promise<number> {
    try {
      const profile = await this.getCompanyProfile();
      return profile?.taxPercentage || 0;
    } catch (error) {
      console.error('Error getting default tax percentage:', error);
      return 0;
    }
  }

  /**
   * Get company location
   */
  static async getCompanyLocation(): Promise<CompanyLocation | null> {
    try {
      const profile = await this.getCompanyProfile();
      return profile?.location || null;
    } catch (error) {
      console.error('Error getting company location:', error);
      return null;
    }
  }

  /**
   * Get company social media links
   */
  static async getCompanySocialMedia(): Promise<CompanySocialMedia | null> {
    try {
      const profile = await this.getCompanyProfile();
      return profile?.socialMedia || null;
    } catch (error) {
      console.error('Error getting company social media:', error);
      return null;
    }
  }

  /**
   * Calculate distance between two coordinates (in kilometers)
   * Using Haversine formula
   */
  static calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  }

  /**
   * Convert degrees to radians
   */
  private static toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Format location for display
   */
  static formatLocation(location: CompanyLocation): string {
    if (location.address) {
      return location.address;
    }
    return `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`;
  }
}